import type { SetStoreFunction } from "solid-js/store";
import type { FormVersion } from "../state/formVersion";
import { useFormVersionDispatch, useFormVersionState } from "../state/formVersion";
import { initialized } from "../utils/initializedUtil";
import { integrationsByType } from "../integrations/integrations";
import IntegrationId from "../integrations/IntegrationId";
import { useTranslation } from "../state/i18n";
import type { Integration, IntegrationType } from "../integrations/types";
import type { IntegrationVerificationResultDto } from "./ApiService";
import ApiService from "./ApiService";
import FormService from "./FormService";

let setFormVersionStore: SetStoreFunction<FormVersion> | null = null;
let formVersionStore: FormVersion | null = null;

const init = () => {
  setFormVersionStore = useFormVersionDispatch();
  formVersionStore = useFormVersionState();
};

const getSetFormVersionStore = () => initialized(setFormVersionStore, "setFormVersionStore");
const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");

export type IntegrationServiceType = {
  getIntegrations: () => Integration[];
  getIntegration: <T = Record<string, unknown>>(id: IntegrationId) => Integration<T> | undefined;
  getIntegrationsByType: <T = Record<string, unknown>>(type: IntegrationType) => Integration<T>[];
  createIntegration: (type: IntegrationType) => Integration;
  addIntegration: (integration: Integration) => void;
  deleteIntegration: (id: IntegrationId) => void;
  moveIntegrationBefore: (id: IntegrationId, beforeIntegrationId: IntegrationId) => void;
  moveIntegrationToEnd: (id: IntegrationId) => void;
  updateName: (id: IntegrationId, value: string) => void;
  updateConfiguration: <T>(id: IntegrationId, key: keyof T, value: unknown) => void;
  verifyIntegration: <T = Record<string, unknown>>(
    integration: Integration<T>,
  ) => Promise<IntegrationVerificationResultDto<T>>;
};

const create = (): IntegrationServiceType => ({
  getIntegrations: () => getFormVersionStore().integrations,
  getIntegration: <T>(id: IntegrationId) =>
    getFormVersionStore().integrations.find((integration) => id.equals(integration.id)) as Integration<T>,
  getIntegrationsByType: <T>(type: IntegrationType) =>
    getFormVersionStore().integrations.filter((integration) => integration.type === type) as Integration<T>[],
  createIntegration: (type: IntegrationType): Integration => {
    const { t } = useTranslation();
    const integrationDefinition = integrationsByType[type];

    return {
      id: IntegrationId.random(),
      type,
      legacy: integrationDefinition.legacy,
      name: t(integrationDefinition.titleKey),
      configuration: integrationDefinition.defaultProperties(),
    };
  },
  addIntegration: (integration: Integration) => {
    getSetFormVersionStore()("integrations", (prev) => [...prev, integration]);
  },
  deleteIntegration: (id: IntegrationId) => {
    getSetFormVersionStore()("integrations", (prev) => prev.filter((integration) => !integration.id.equals(id)));
  },
  moveIntegrationBefore: (id: IntegrationId, beforeIntegrationId: IntegrationId) => {
    getSetFormVersionStore()("integrations", (prev) => {
      if (id.equals(beforeIntegrationId)) {
        return prev;
      }

      const from = prev.findIndex((integration) => integration.id.equals(id));
      const to = prev.findIndex((integration) => integration.id.equals(beforeIntegrationId));
      if (from === -1 || to === -1) {
        return prev;
      }

      const toMove = prev[from];
      const listWithout = [...prev.slice(0, from), ...prev.slice(from + 1)];
      const newIndex = to > from ? to - 1 : to;

      return [...listWithout.slice(0, newIndex), toMove, ...listWithout.slice(newIndex)];
    });
  },
  moveIntegrationToEnd: (id: IntegrationId) => {
    getSetFormVersionStore()("integrations", (prev) => {
      const from = prev.findIndex((integration) => integration.id.equals(id));
      const toMove = prev[from];
      if (from === -1) {
        return prev;
      }

      const listWithout = [...prev.slice(0, from), ...prev.slice(from + 1)];

      return [...listWithout, toMove];
    });
  },
  updateName: (id: IntegrationId, value: string) => {
    getSetFormVersionStore()("integrations", (integration) => id.equals(integration.id), "name", value);
  },
  updateConfiguration: <T>(id: IntegrationId, key: keyof T, value: unknown) => {
    getSetFormVersionStore()(
      "integrations",
      (integration) => id.equals(integration.id),
      "configuration",
      key.toString(),
      value,
    );
  },
  verifyIntegration: async <T>(integration: Integration<T>): Promise<IntegrationVerificationResultDto<T>> => {
    const formVersion = FormService.getFormVersion();
    return await ApiService.verifyIntegration(integration, formVersion);
  },
});

export default {
  init,
  create,
  getIntegration: create().getIntegration,
};
